import { RECEIPT_REASON_GUIDANCE } from 'src/utils/receiptDiscrepancyGuidance'
import React from 'react'
import styled from 'styled-components'
import { Checkbox, Input, InputNumber, Select, Tag } from 'antd'
import {
  getOrderHasVatDisplay,
  getOrderVatTreatmentDisplay,
  isSupplierRejectedLine,
  shouldShowWaitingOnSupplierApproval,
} from 'src/utils/goodsReceiptingLineHelpers'
import {
  OMS_VAT_TREATMENT,
  listOmsVatTreatmentOptions,
} from 'src/utils/vatTreatment'
import {
  describeCatchWeightReceiptSum,
  getReceiptingLineVatBreakdownCopy,
  resolveStoredOrderLineVat,
} from 'src/utils/manualOrderVatHelpers'
import {
  applyAsOrderedToggle,
  applyReportErrorToggle,
  isLineAsOrdered,
} from 'src/utils/goodsReceiptingAsOrderedHelpers'
import {
  canEditGrLineCheckIn,
  isGrLineOutcomeResolved,
} from 'src/utils/goodsReceiptingActionHelpers'
import { getReceiptingEditTypeLabel } from 'src/utils/receiptingEditTypeLabels'
import {
  GR_APP_CHECK_IN_COPY,
  shouldShowAppCheckInAffordance,
} from 'src/utils/goodsReceiptingAppCheckInBridgeHelpers'
import {
  getDefaultAskForCredit,
  getReceiptingLineStatusForCredit,
  resolveAskForCredit,
} from 'src/utils/goodsReceiptingCreditHelpers'
import { shouldShowAddedOnReceiptingBadge } from 'src/utils/goodsReceiptingActionHelpers'
import {
  canAddGrLineToShoppingList,
  findVenueProductForOrderLine,
  isReceiptingLineExcludedFromGrossProfit,
  orderLineWithParentSupplier,
} from 'src/utils/goodsReceiptingExcludeFromGrossProfit'
import ExcludeFromGrossProfitTick from 'src/screens/venue-products/ExcludeFromGrossProfitTick'
import {
  GR_EXCLUDE_FROM_GP_ADD_TO_LIST_TOOLTIP,
  GR_EXCLUDE_FROM_GP_UNAVAILABLE_TOOLTIP,
} from 'src/utils/excludeFromGrossProfit'
import { ExcludeFromGrossProfitColumnHeader } from 'src/utils/venueProductFieldTooltips'
import { resolveReceivedQuantityDisplay } from 'src/utils/resolveReceivedQuantityDisplay'
import { isWeightPriced } from 'src/utils/priceFunctions'
import ReportProductIssueButton from 'src/components/productIssue/ReportProductIssueButton'
import { PRODUCT_ISSUE_SOURCE } from 'src/utils/productIssue'
const { Option } = Select

/** Ant Design 3 Select labels (options= prop is AD4-only). */
const VAT_TREATMENT_SELECT_LABELS = {
  standard: 'Standard-rated — 20% VAT',
  zeroRated: 'Zero-rated — 0% VAT',
  noVat: 'No VAT — outside scope of VAT',
}

const getStatusColor = (editType, cnRequestStatus) => {
  if (editType === 'credit_note_request') {
    switch (cnRequestStatus) {
      case 'approved':
        return 'green'
      case 'rejected':
        return 'red'
      case 'pending':
        return 'orange'
      default:
        return 'blue'
    }
  }

  switch (editType) {
    case 'reconciliation':
      return 'green'
    case 'full_accept':
      return 'green'
    default:
      return 'orange'
  }
}

const getStatusText = (editType, cnRequestStatus) => {
  if (editType === 'credit_note_request') {
    // Only show approved when cnRequestStatus is actually 'approved'
    if (cnRequestStatus === 'approved') {
      return 'CN Approved'
    }
    if (!cnRequestStatus) {
      return 'CN Requested'
    }
    // For all other cases, show the request status
    switch (cnRequestStatus) {
      case 'rejected':
        return 'CN Rejected'
      case 'pending':
        return 'CN Requested'
      default:
        return 'CN Requested'
    }
  }

  switch (editType) {
    case 'reconciliation':
      return 'Reconciled'
    case 'full_accept':
      return 'Full Accept'
    default:
      // Ticket guard: never leak raw internal enums (e.g.
      // invoice_reconciliation) into the customer UI.
      return getReceiptingEditTypeLabel(editType)
  }
}

const ProductColumns = (
  errors,
  setErrors,
  isEditable,
  weightedItems,
  setWeightedItems,
  openHistory,
  asOrderedLineIds = [],
  setAsOrderedLineIds = () => {},
  parentOrder = null,
  orderHasVatDraft = {},
  onHasVatChange = null,
  excludeFromGp = {},
) => {
  const venueProducts = excludeFromGp.venueProducts || []
  const onExcludeFromGrossProfitToggle =
    excludeFromGp.onExcludeFromGrossProfitToggle
  const onAddUnmatchedLineToShoppingList =
    excludeFromGp.onAddUnmatchedLineToShoppingList
  const addingShoppingListLineId = excludeFromGp.addingShoppingListLineId
  /** Retrospective receipt editing: this PO is reopened by an Admin / GM. */
  const retroEditActive = Boolean(excludeFromGp.retroEditActive)
  const venueId = excludeFromGp.venueId

  const renderProductIssueButton = (record, { compact = false } = {}) => (
    <ReportProductIssueButton
      venueId={venueId}
      order={record}
      product={record?.product}
      supplierTitle={
        parentOrder?.supplier?.title ||
        record?.product?.supplier?.title ||
        parentOrder?.supplierTitle ||
        ''
      }
      sourceScreen={PRODUCT_ISSUE_SOURCE.RECEIPTING}
      iconOnly={compact}
      type='dashed'
      size='small'
      label={compact ? 'Report product data issue' : 'Report product data'}
      tooltipPlacement='left'
      className={compact ? undefined : 'gr-product-issue-btn'}
    />
  )

  const renderEditableActions = record => {
    const locallyAsOrdered = isLineAsOrdered(asOrderedLineIds, record)
    const hasLocalReportError = !!errors.find(
      error => error.order === record._id,
    )
    const showFromApp = shouldShowAppCheckInAffordance(
      record,
      {
        locallyAsOrdered,
        hasLocalReportError,
      },
      parentOrder || {},
    )
    const errorEntry = errors.find(error => error.order === record._id)

    const showOutcomeNeeded =
      !locallyAsOrdered &&
      !hasLocalReportError &&
      !isGrLineOutcomeResolved(record, errors, asOrderedLineIds)

    return (
      <ActionWrapper>
        {(showFromApp || showOutcomeNeeded) && (
          <ActionRow>
            {showFromApp ? (
              <Tag color='blue'>{GR_APP_CHECK_IN_COPY.fromAppChip}</Tag>
            ) : null}
            {showOutcomeNeeded ? (
              <Tag color='orange' style={{ margin: 0 }}>
                Outcome needed
              </Tag>
            ) : null}
          </ActionRow>
        )}

        <ActionRow>
          <Checkbox
            className='checkbox'
            onChange={e => isAsOrderedCheck(e, record)}
            checked={locallyAsOrdered}
          >
            As ordered
          </Checkbox>
          <Checkbox
            className='checkbox'
            onChange={e => isErrorCheck(e, record)}
            checked={hasLocalReportError}
            disabled={locallyAsOrdered}
          >
            Report Error
          </Checkbox>
        </ActionRow>

        <Select
          className='gr-error-select'
          style={{ width: '100%' }}
          onSelect={value => onErrorSelect(value, record)}
          disabled={!errorEntry || locallyAsOrdered}
          value={errorEntry ? errorEntry.error : ''}
        >
          {errorOptions.map(item => (
            <Option key={item} value={item} style={{ whiteSpace: 'normal' }}>
              {item}
            </Option>
          ))}
        </Select>

        <ActionRow className='gr-secondary-actions'>
          <History onClick={() => openHistory(record._id)}>History</History>
          {renderProductIssueButton(record)}
        </ActionRow>

        <ActionRow>
          <Checkbox
            disabled={
              !errorEntry ||
              !String(errorEntry?.error || '').trim() ||
              locallyAsOrdered
            }
            checked={resolveAskForCredit(errorEntry || {})}
            onChange={e => onAskForCreditChange(e.target.checked, record)}
          >
            Ask for credit?
          </Checkbox>
        </ActionRow>

        <Input
          className='notes'
          onChange={e => onDetailsChange(e, record, 'note')}
          value={notesInputValue(record)}
          disabled={!errorEntry || locallyAsOrdered}
          placeholder='Add details about error product, please...'
        />
        {hasLocalReportError ? (
          <GuidanceNote>
            {RECEIPT_REASON_GUIDANCE[errorEntry?.error]}
            <div>
              Ask for credit controls whether a supplier credit request is sent.
              It does not confirm that a credit has been received.
            </div>
          </GuidanceNote>
        ) : null}
      </ActionWrapper>
    )
  }
  const isVatControlsDisabled = record =>
    !onHasVatChange ||
    isSupplierRejectedLine(record) ||
    record?.isVoided === true ||
    Boolean(parentOrder?.isVoided) ||
    record.status === 'Rejected by supplier'

  const isLineWeightPriced = record =>
    isWeightPriced(record) ||
    isWeightPriced(record.product) ||
    record?.receipting?.error === 'Weighted item'

  const weightUnitLabel = record => {
    const raw = [
      record?.UoM,
      record?.pricedBy,
      record?.product?.UoM,
      record?.product?.pricedBy,
    ].find(value => typeof value === 'string' && value.trim())
    const token = String(raw || '').toLowerCase()
    if (/(^|[^a-z])(kgs?|kilos?|kilograms?)([^a-z]|$)/.test(token)) return 'kg'
    if (/(^|[^a-z])(gms?|grams?)([^a-z]|$)/.test(token) || token.trim() === 'g')
      return 'g'
    if (/(^|[^a-z])(lbs?|pounds?)([^a-z]|$)/.test(token)) return 'lb'
    if (/(^|[^a-z])(oz|ounces?)([^a-z]|$)/.test(token)) return 'oz'
    return 'kg'
  }

  const catchWeightSum = record => {
    if (!isLineWeightPriced(record)) return ''
    return describeCatchWeightReceiptSum({
      weight: getLineQuantityForTotal(record),
      unitPrice: resolveUnitPrice(record),
      unitLabel: weightUnitLabel(record),
    })
  }

  const getLineQuantityForTotal = record => {
    // Catch-weight: a drafted billable weight IS the pricing quantity, so it
    // drives the live Total (weight × /kg price) — same rule as the server.
    if (isLineWeightPriced(record)) {
      const weighted = weightedItems.find(item => item.order === record._id)
      const weight = Number(weighted?.weight)
      if (Number.isFinite(weight) && weight > 0) {
        return weight
      }
    }
    const currentField = errors.find(error => error.order === record._id)
    if (
      currentField?.updatedQuantity != null &&
      Number.isFinite(Number(currentField.updatedQuantity))
    ) {
      return Number(currentField.updatedQuantity)
    }
    return resolveReceivedQuantityDisplay(record)
  }

  const renderVatColumn = (_, record) => {
    const orderId = String(record?._id || '')
    const hasVatDraftValue = Object.prototype.hasOwnProperty.call(
      orderHasVatDraft,
      orderId,
    )
      ? orderHasVatDraft[orderId]
      : undefined
    const treatment = getOrderVatTreatmentDisplay(record, hasVatDraftValue)
    const hasVat = treatment === OMS_VAT_TREATMENT.STANDARD
    const disabled = isVatControlsDisabled(record)
    const exVatUnit = resolveUnitPrice(record)
    const quantity = getLineQuantityForTotal(record)
    const copy = getReceiptingLineVatBreakdownCopy({
      exVatUnitPrice: exVatUnit,
      hasVat,
      priceIncludesVat: false,
      quantity,
    })

    return (
      <VatColumnWrap>
        <VatToggleBlock>
          <VatFieldLabel>VAT treatment</VatFieldLabel>
          <VatTreatmentSelect
            size='small'
            disabled={disabled || !onHasVatChange}
            value={treatment}
            style={{ width: '100%', minWidth: 0 }}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 320, maxWidth: 400 }}
            onChange={value => onHasVatChange(value, record)}
          >
            {listOmsVatTreatmentOptions().map(opt => (
              <Option key={opt.value} value={opt.value}>
                {VAT_TREATMENT_SELECT_LABELS[opt.value] || opt.label}
              </Option>
            ))}
          </VatTreatmentSelect>
        </VatToggleBlock>

        {hasVat && !catchWeightSum(record) ? (
          <VatBreakdownPanel>
            <div>{copy.enteredLine}</div>
            {copy.exVatLine ? <div>{copy.exVatLine}</div> : null}
            {copy.vatLine ? <div>{copy.vatLine}</div> : null}
            <VatTotalLine>{copy.totalLine}</VatTotalLine>
            {copy.lineTotalLine ? <div>{copy.lineTotalLine}</div> : null}
          </VatBreakdownPanel>
        ) : null}
      </VatColumnWrap>
    )
  }

  const errorOptions = [
    'Damaged',
    'Quality Issue',
    'Not Delivered',
    'Something Else',
    'Substitution',
    'Supplier / Invoice adjustment',
    'Weighted item',
    'Read Only',
  ]
  const isAlternativeReceived = record => {
    const current = errors.find(item => item?.order === record._id)
    return current ? current.error === 'Substitution' : false
  }

  const syncLineEdits = next => {
    setAsOrderedLineIds(next.asOrderedLineIds)
    setErrors(next.editedReceipting)
  }

  const isAsOrderedCheck = (e, record) => {
    syncLineEdits(
      applyAsOrderedToggle({
        asOrderedLineIds,
        editedReceipting: errors,
        recordOrId: record,
        checked: e.target.checked,
      }),
    )
  }

  const isErrorCheck = (e, record) => {
    const checked = e.target.checked
    syncLineEdits(
      applyReportErrorToggle({
        asOrderedLineIds,
        editedReceipting: errors,
        recordOrId: record,
        checked,
        nextErrorRow: checked
          ? {
              order: record._id,
              error: '',
              askForCredit: false,
              supplier: record.supplier,
              purchaseOrderNumber: record.purchaseOrderNumber,
              status: getReceiptingLineStatusForCredit(false),
            }
          : null,
      }),
    )
  }

  const onAskForCreditChange = (checked, record) => {
    const current = errors.find(item => item.order === record._id)
    if (!current) return
    const askForCredit = Boolean(checked)
    const updatedOrder = {
      ...current,
      askForCredit,
      status: getReceiptingLineStatusForCredit(askForCredit),
    }
    setErrors(
      errors.map(item => (item.order === record._id ? updatedOrder : item)),
    )
  }

  const onDetailsChange = (e, record, field) => {
    const currentOrder = errors.find(item => {
      return item.order === record._id
    })
    const currentWeightedItem = weightedItems.find(
      item => item.order === record._id,
    )
    let updatedOrder
    switch (field) {
      case 'title':
        updatedOrder = {
          ...currentOrder,
          updatedTitle: e.target.value,
          orderedTitle: record.orderedTitle || record.title,
          prevTitle: record.receipting?.updatedTitle || record.title,
        }
        setErrors(
          errors.map(item => (item.order === record._id ? updatedOrder : item)),
        )
        break
      case 'price':
        updatedOrder = {
          ...currentOrder,
          updatedPrice: e * 100,
          orderedPrice: record.orderedPrice || record.price,
          prevPrice: record.receipting?.updatedPrice || record.price,
        }
        setErrors(
          errors.map(item => (item.order === record._id ? updatedOrder : item)),
        )
        break
      case 'size':
        updatedOrder = {
          ...currentOrder,
          updatedSize: e.target.value,
          orderedSize: record.orderedSize || record.size,
          prevSize: record.receipting?.updatedSize || record.size,
        }
        setErrors(
          errors.map(item => (item.order === record._id ? updatedOrder : item)),
        )
        break
      case 'qty': {
        // Received Qty is always the absolute quantity the user typed.
        // (Historical `qty - e` difference path stored shortage as
        // updatedQuantity and corrupted Order.quantity on save, e.g. 1−5 → −4.)
        const orderedQty =
          record.receipting?.orderedQuantity ??
          record.orderedQuantity ??
          record.quantity
        const priorReceived =
          record.receipting?.updatedQuantity ??
          record.receipting?.previousQty ??
          orderedQty
        updatedOrder = {
          ...currentOrder,
          updatedQuantity: e,
          orderedQuantity: orderedQty,
          previousQty: priorReceived,
        }
        setErrors(
          errors.map(item => (item.order === record._id ? updatedOrder : item)),
        )
        break
      }
      case 'sku':
        updatedOrder = {
          ...currentOrder,
          updatedSku: e.target.value,
          orderedSku: record.orderedSku || record.sku,
          prevSku: record.receipting?.updatedSku || record.sku,
        }
        setErrors(
          errors.map(item => (item.order === record._id ? updatedOrder : item)),
        )
        break
      case 'note':
        updatedOrder = { ...currentOrder, notes: e.target.value }
        setErrors(
          errors.map(item => (item.order === record._id ? updatedOrder : item)),
        )
        break
      case 'weight':
        if (!currentWeightedItem) {
          setWeightedItems([
            ...weightedItems,
            {
              order: record._id,
              error: 'Weighted item',
              supplier: record.supplier,
              purchaseOrderNumber: record.purchaseOrderNumber,
              status: 'Accepted',
              weight: e,
              orderedQuantity: record.orderedQuantity || record.quantity,
              // Snapshot the pricing unit so the server stores the billable
              // weight's UoM explicitly (e.g. "kg") instead of inferring.
              ...(isLineWeightPriced(record)
                ? {
                    billableUoM: record.UoM || record.product?.UoM || undefined,
                  }
                : {}),
            },
          ])
        } else {
          e !== record.quantity
            ? setWeightedItems(
                weightedItems.map(item => {
                  return item.order !== record._id
                    ? item
                    : { ...currentWeightedItem, weight: e }
                }),
              )
            : setWeightedItems(
                weightedItems.filter(item => item.order !== record._id),
              )
        }
        break
      default:
        break
    }
  }

  const notesInputValue = record => {
    const notes = errors.find(item => {
      return item.order === record._id
    })?.notes
    return notes ? notes : ''
  }

  const onErrorSelect = (value, record) => {
    const errorProduct = errors.find(error => error.order === record._id)
    if (!errorProduct) return

    // Mixed credit + non-credit lines on one PO are allowed.
    // Ask for credit? defaults by reason; checkbox always wins after that.
    const askForCredit = getDefaultAskForCredit(value)
    errorProduct.error = value
    errorProduct.askForCredit = askForCredit
    errorProduct.status = getReceiptingLineStatusForCredit(askForCredit)
    setErrors(
      errors.map(error =>
        error.order === errorProduct.order ? errorProduct : error,
      ),
    )
  }

  const resolveUnitPrice = record => {
    if (record.status === 'Rejected by supplier') {
      const rejectedPrice = Number(record.price ?? record.product?.price)
      return Number.isFinite(rejectedPrice) ? rejectedPrice : 0
    }

    if (
      record.receipting?.updatedPrice != null &&
      !Number.isNaN(Number(record.receipting.updatedPrice))
    ) {
      return Number(record.receipting.updatedPrice)
    }

    const brakesPrice = Number(record.brakesPrice)
    if (Number.isFinite(brakesPrice) && brakesPrice > 0) {
      return brakesPrice
    }

    const orderPrice = Number(record.price)
    if (
      Number.isFinite(orderPrice) &&
      record.price != null &&
      record.price !== ''
    ) {
      return orderPrice
    }

    const productPrice = Number(record.product?.price)
    return Number.isFinite(productPrice) ? productPrice : 0
  }

  const selectDataIndex = (record, field) => {
    switch (field) {
      case 'title': {
        return record.status !== 'Rejected by supplier'
          ? record.receipting?.updatedTitle ||
              record.title ||
              record.product.title
          : record.title || record.product.title
      }
      case 'price': {
        return resolveUnitPrice(record)
      }
      case 'size': {
        return record.status !== 'Rejected by supplier'
          ? record.receipting?.updatedSize || record.size || record.product.size
          : record.size || record.product.size
      }
      case 'quantity': {
        // Received qty display — never prefer previousQty (audit-only; can be
        // stale/negative and hide a correctly saved updatedQuantity).
        return resolveReceivedQuantityDisplay(record)
      }
      case 'sku': {
        return record.status !== 'Rejected by supplier'
          ? record.receipting?.updatedSku || record.sku || record.product.sku
          : record.sku || record.product.sku
      }
      case 'updatedQuantity': {
        return resolveReceivedQuantityDisplay(record)
      }
    }
  }

  return [
    {
      title: 'Title',
      dataIndex: 'title' || 'product.title',
      render: (text, record) => {
        const title = selectDataIndex(record, 'title')
        const titleNode = !isAlternativeReceived(record) ? (
          title
        ) : (
          <Input
            defaultValue={title}
            onChange={e => onDetailsChange(e, record, 'title')}
          />
        )
        return (
          <TitleCell>
            <span>{titleNode}</span>
            {isSupplierRejectedLine(record) ? (
              <Tag color='red' style={{ margin: 0 }}>
                Rejected
              </Tag>
            ) : null}
            {shouldShowAddedOnReceiptingBadge(record) ? (
              <Tag color='blue' style={{ margin: 0 }}>
                Added on receipting
              </Tag>
            ) : null}
          </TitleCell>
        )
      },
    },
    {
      title: 'Actions',
      dataIndex: 'status',
      className: 'gr-line-actions-col',
      width: 280,
      render: (status, record) => {
        if (shouldShowWaitingOnSupplierApproval(record)) {
          return <Tag color={'orange'}>Waiting on Supplier Approval</Tag>
        }

        const canEditCheckIn = canEditGrLineCheckIn(record, parentOrder || {}, {
          retrospective: retroEditActive,
        })

        if (!canEditCheckIn) {
          if (isSupplierRejectedLine(record)) {
            return (
              <ReadOnlyActions>
                <Tag color='red'>Rejected</Tag>
                {renderProductIssueButton(record)}
              </ReadOnlyActions>
            )
          }
          if ((record.status || '') === 'Full accept') {
            return (
              <ReadOnlyActions>
                <Tag color={getStatusColor('full_accept', 'Full Accept')}>
                  Full Accept
                </Tag>
                {renderProductIssueButton(record)}
              </ReadOnlyActions>
            )
          }
          const editType = record.receipting?.editType
          if (editType) {
            const cnRequestStatus = record.receipting?.cnRequestStatus
            return (
              <ReadOnlyActions>
                <Tag color={getStatusColor(editType, cnRequestStatus)}>
                  {getStatusText(editType, cnRequestStatus)}
                </Tag>
                {renderProductIssueButton(record)}
              </ReadOnlyActions>
            )
          }
          return renderProductIssueButton(record)
        }

        return renderEditableActions(record)
      },
    },
    {
      title: <ExcludeFromGrossProfitColumnHeader />,
      key: 'excludeFromGrossProfit',
      className: 'excludeFromGpColumn',
      align: 'center',
      width: 110,
      render: (_, record) => {
        const line = orderLineWithParentSupplier(record, parentOrder)
        const venueProduct = findVenueProductForOrderLine(venueProducts, line)
        if (!venueProduct) {
          const canAdd = canAddGrLineToShoppingList(line)
          return (
            <ExcludeFromGrossProfitTick
              disabled
              checked={false}
              unavailableTooltip={
                canAdd
                  ? GR_EXCLUDE_FROM_GP_ADD_TO_LIST_TOOLTIP
                  : GR_EXCLUDE_FROM_GP_UNAVAILABLE_TOOLTIP
              }
              onAddToShoppingList={
                canAdd
                  ? () => onAddUnmatchedLineToShoppingList?.(line)
                  : undefined
              }
              addToShoppingListBusy={
                String(addingShoppingListLineId || '') === String(record._id)
              }
            />
          )
        }

        return (
          <ExcludeFromGrossProfitTick
            product={venueProduct}
            products={venueProducts}
            checked={isReceiptingLineExcludedFromGrossProfit(
              venueProducts,
              line,
            )}
            onChange={() => onExcludeFromGrossProfitToggle?.(line)}
          />
        )
      },
    },
    {
      title: 'Item Price',
      dataIndex: 'price' || 'product.price',
      render: (text, record) => {
        const price = selectDataIndex(record, 'price')
        return !isEditable(record, 'price') ? (
          `£${price.toFixed(2)}`
        ) : (
          <InputNumber
            defaultValue={Number(price.toFixed(2))}
            onChange={e => onDetailsChange(e, record, 'price')}
            min={0}
          />
        )
      },
    },
    {
      title: 'VAT',
      dataIndex: 'hasVat',
      width: 160,
      render: renderVatColumn,
    },
    {
      title: 'Total Price',
      width: 280,
      render: (_, record) => {
        const orderId = String(record?._id || '')
        const hasVatDraftValue = Object.prototype.hasOwnProperty.call(
          orderHasVatDraft,
          orderId,
        )
          ? orderHasVatDraft[orderId]
          : undefined
        const hasVat = getOrderHasVatDisplay(record, hasVatDraftValue)
        const exVatUnit = resolveUnitPrice(record)
        const quantity = getLineQuantityForTotal(record)
        const breakdown = resolveStoredOrderLineVat({
          exVatUnitPrice: exVatUnit,
          hasVat,
          quantity,
        })
        const weightSum = catchWeightSum(record)

        return (
          <TotalPriceCell>
            {weightSum ? (
              <strong>{weightSum}</strong>
            ) : (
              <>
                {hasVat ? (
                  <>
                    <div>Ex VAT: £{breakdown.exVatLine.toFixed(2)}</div>
                    <div>VAT: £{breakdown.vatLine.toFixed(2)}</div>
                  </>
                ) : null}
                <strong>Total: £{breakdown.grossLine.toFixed(2)}</strong>
              </>
            )}
          </TotalPriceCell>
        )
      },
    },
    {
      title: 'Size',
      width: '10%',
      dataIndex: 'size' || 'product.size',
      render: (text, record) => {
        const size = selectDataIndex(record, 'size')
        return !isAlternativeReceived(record) ? (
          size
        ) : (
          <Input
            defaultValue={size}
            onChange={e => onDetailsChange(e, record, 'size')}
          />
        )
      },
    },
    {
      title: 'Ordered Qty',
      dataIndex: 'orderedQuantity',
      width: '10%',
      render: (text, record) => {
        // Prefer receipting/order orderedQuantity so a bad received-qty
        // overwrite of Order.quantity never masquerades as ordered.
        if (
          record?.receipting?.orderedQuantity != null &&
          !Number.isNaN(Number(record.receipting.orderedQuantity))
        ) {
          return Number(record.receipting.orderedQuantity)
        }
        if (text != null && text !== '' && !Number.isNaN(Number(text))) {
          return Number(text)
        }
        return record.quantity
      },
    },
    {
      title: 'Received Qty',
      width: '10%',
      render: (_, record) => {
        const draft = errors.find(error => error.order === record._id)
        const qty =
          draft?.updatedQuantity != null &&
          Number.isFinite(Number(draft.updatedQuantity))
            ? Number(draft.updatedQuantity)
            : resolveReceivedQuantityDisplay(record)
        const isErrorSelected = !!draft?.error
        return isEditable(record, 'qty') ? (
          <InputNumber
            value={qty}
            disabled={!isErrorSelected}
            onChange={e => onDetailsChange(e, record, 'qty')}
            min={0}
          />
        ) : (
          qty
        )
      },
    },
    {
      title: 'SKU',
      dataIndex: 'sku' || 'product.sku',
      width: '10%',
      render: (text, record) => {
        const sku = selectDataIndex(record, 'sku')
        return !isAlternativeReceived(record) ? (
          sku
        ) : (
          <Input
            defaultValue={sku}
            onChange={e => onDetailsChange(e, record, 'sku')}
          />
        )
      },
    },
    {
      title: 'Weighted',
      dataIndex: 'UoM',
      render: (UoM, record) => {
        // Weight-priced detection via the shared catalogue helper (UoM /
        // pricedBy), not just the literal 'Kg' — covers 'kg', 'per kg',
        // 'Kilogram', 'lb' etc. Non-weight products stay 'Not Weighted'.
        if (!isLineWeightPriced(record)) {
          return 'Not Weighted'
        }
        const unitLabel = UoM || record.product?.UoM || 'Kg'
        const weight =
          record.receipting?.weight ??
          record.receipting?.previousQty ??
          record.quantity
        return isEditable(record, 'weight') ? (
          <>
            <InputNumber
              defaultValue={weight}
              onChange={e => onDetailsChange(e, record, 'weight')}
              min={0}
            />
            <span>{unitLabel}</span>
          </>
        ) : (
          `${weight} ${unitLabel}`
        )
      },
    },
    {
      title: 'Receipting Status',
      dataIndex: 'receipting.editType',
      width: '15%',
      render: (editType, record) => {
        const locallyAsOrdered = isLineAsOrdered(asOrderedLineIds, record)
        if (record.status === 'Full accept' || locallyAsOrdered) {
          return (
            <Tag color={getStatusColor('full_accept', 'Full Accept')}>
              Full Accept
            </Tag>
          )
        }

        if (!record.receipting || !editType) {
          return null
        }

        const cnRequestStatus = record.receipting?.cnRequestStatus

        return (
          <Tag color={getStatusColor(editType, cnRequestStatus)}>
            {getStatusText(editType, cnRequestStatus)}
          </Tag>
        )
      },
    },
    {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      className: 'restrict-shrink',
      width: '10%',
      render: invoiceNumber => invoiceNumber || '-',
    },
  ]
}

const ActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  width: 100%;

  .notes,
  .gr-error-select {
    width: 100% !important;
    margin-left: 0 !important;
  }

  /* antd v3 stacks sibling checkboxes with margin-left — reset so rows align */
  .ant-checkbox-wrapper + .ant-checkbox-wrapper {
    margin-left: 0;
  }

  .gr-secondary-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    align-items: stretch;
  }

  .gr-secondary-actions .gr-product-issue-btn,
  .gr-secondary-actions .ant-btn {
    width: 100%;
    font-weight: 400 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  .gr-secondary-actions .gr-product-issue-btn .anticon {
    color: #52c41a;
  }
`

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
`

const ReadOnlyActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const GuidanceNote = styled.div`
  margin-top: 8px;
  max-width: 420px;
  white-space: normal;
  overflow-wrap: anywhere;
  font-size: 12px;
`

const History = styled.button.attrs({ type: 'button' })`
  cursor: pointer;
  color: rgba(0, 0, 0, 0.65);
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  height: 32px;
  width: 100%;
  padding: 0 8px;
  font-size: 13px;
  line-height: 30px;
  text-align: center;
  white-space: nowrap;

  &:hover {
    color: #40a9ff;
    border-color: #40a9ff;
  }
`

const TitleCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const VatColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
`

const VatToggleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const VatFieldLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
`

const VatBreakdownPanel = styled.div`
  padding: 8px 10px;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  font-size: 12px;
  line-height: 1.6;
`

const VatTotalLine = styled.div`
  font-weight: 600;
  margin-top: 2px;
`

const TotalPriceCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
`
const VatTreatmentSelect = styled(Select)`
  .ant-select-selection-selected-value {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export default ProductColumns
